import { useState, useEffect } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import { toast } from "react-toastify";
import {
  PageContainer, DataTable, PremiumModal, PremiumCard, Button,
  Field, SelectField, StatusBadge, EmptyState,
} from "components/ui";

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const BranchUsersPage = () => {
  const axiosPrivate = useAxiosPrivate();
  const { user } = useCurrentUser();
  const branch = user?.branch;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    password: "", role: "STOCK_MANAGER", active: true,
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = async () => {
    if (!branch) return;
    try {
      const res = await axiosPrivate.get(`/api/branches/branch/${branch.id}/users`);
      setUsers(res.data);
    } catch (_) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [branch]);

  const openModal = (u = null) => {
    if (u) {
      setEditingUser(u);
      setForm({
        firstName: u.firstName || "", lastName: u.lastName || "",
        email: u.email || "", phone: u.phone || "",
        password: "", role: u.role || "STOCK_MANAGER", active: u.active,
      });
    } else {
      setEditingUser(null);
      setForm({
        firstName: "", lastName: "", email: "", phone: "",
        password: "", role: "STOCK_MANAGER", active: true,
      });
    }
    setModal(true);
  };

  const handleSubmit = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      toast.warn("First name, last name, and email are required.");
      return;
    }
    if (!editingUser && !form.password.trim()) {
      toast.warn("Password is required for new users.");
      return;
    }
    setSubmitting(true);
    try {
      if (editingUser) {
        await axiosPrivate.put(`/api/users/${editingUser.id}`, {
          firstName: form.firstName, lastName: form.lastName,
          email: form.email, phone: form.phone,
          role: form.role, active: form.active,
        });
        toast.success("User updated");
      } else {
        await axiosPrivate.post("/api/users/branch-user", {
          firstName: form.firstName, lastName: form.lastName,
          email: form.email, phone: form.phone,
          password: form.password, role: form.role,
        });
        toast.success("User created");
      }
      setModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await axiosPrivate.patch(`/api/users/${u.id}/toggle-active`);
      toast.success(`User ${u.active ? "deactivated" : "activated"}`);
      fetchUsers();
    } catch (_) {
      toast.error("Failed to toggle status");
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.firstName} ${u.lastName}?`)) return;
    try {
      await axiosPrivate.delete(`/api/users/${u.id}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  if (!branch) {
    return (
      <PageContainer>
        <PremiumCard>
          <EmptyState title="No branch assigned" description="Ask your tenant admin to assign you to a branch." />
        </PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <DataTable
        title="Branch users"
        subtitle={`Team in ${branch.name}`}
        toolbar={
          <Button variant="primary" size="sm" leftIcon={<IconPlus />} onClick={() => openModal()}>
            Add user
          </Button>
        }
        loading={loading}
        data={users}
        rowKey={(u) => u.id}
        searchPlaceholder="Search by name or email…"
        pageSize={10}
        emptyTitle="No users in this branch yet"
        emptyAction={<Button onClick={() => openModal()} leftIcon={<IconPlus />}>Add user</Button>}
        columns={[
          {
            key: "name", header: "Name",
            render: (u) => <strong>{u.firstName} {u.lastName}</strong>,
            searchable: false,
          },
          { key: "email", header: "Email", render: (u) => <span className="t-muted">{u.email}</span> },
          { key: "role",  header: "Role",  render: (u) => <StatusBadge value={u.role} /> },
          { key: "active", header: "Status", render: (u) => <StatusBadge value={u.active ? "ACTIVE" : "INACTIVE"} dot /> },
          {
            key: "actions", header: "", align: "right",
            render: (u) => (
              <div style={{ display: "inline-flex", gap: 6 }}>
                <Button variant="secondary" size="sm" onClick={() => openModal(u)}>Edit</Button>
                <Button variant={u.active ? "ghost" : "primary"} size="sm" onClick={() => toggleActive(u)}>
                  {u.active ? "Deactivate" : "Activate"}
                </Button>
                <Button variant="danger" size="sm" onClick={() => handleDelete(u)}>Delete</Button>
              </div>
            ),
          },
        ]}
      />

      <PremiumModal
        isOpen={modal}
        toggle={() => setModal(false)}
        title={editingUser ? "Edit user" : "New user"}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" loading={submitting} onClick={handleSubmit}>
              {editingUser ? "Save changes" : "Create user"}
            </Button>
          </>
        }
      >
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <Field label="First name" required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} placeholder="John" />
          <Field label="Last name"  required value={form.lastName}  onChange={(e) => setForm({ ...form, lastName: e.target.value })}  placeholder="Doe" />
          <Field label="Email"      required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john.doe@branch.com" />
          <Field label="Phone"      type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+216 …" />
          {!editingUser && (
            <Field
              label="Password" required type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Enter a secure password"
              autoComplete="new-password"
            />
          )}
          <SelectField label="Role" required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="STOCK_MANAGER">Stock Manager</option>
            <option value="CASHIER">Cashier</option>
            <option value="RH">RH</option>
            <option value="ACCOUNTANT">Accountant</option>
          </SelectField>
          {editingUser && (
            <SelectField
              label="Status"
              value={form.active ? "active" : "inactive"}
              onChange={(e) => setForm({ ...form, active: e.target.value === "active" })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </SelectField>
          )}
        </div>
      </PremiumModal>
    </PageContainer>
  );
};

export default BranchUsersPage;
